import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CurrencyContext = createContext();

// Free API for real-time exchange rates (USD base)
const EXCHANGE_API = 'https://open.er-api.com/v6/latest/USD';
const CACHE_KEY = 'asmarshop-exchange-rates';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Backend store-info API for admin override rates
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://asmarshop-backend.up.railway.app/api/v1';

export const CurrencyProvider = ({ children }) => {
  const [rates, setRates] = useState(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < CACHE_DURATION) {
          return parsed.rates;
        }
      }
    } catch (e) {}
    return { BRL: 5.20, PYG: 7300 }; // sensible fallback
  });
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [rateSource, setRateSource] = useState('cache'); // 'admin' | 'api' | 'cache' | 'fallback'

  const fetchRates = useCallback(async () => {
    try {
      setLoading(true);

      // Step 1: Check if admin set manual override rates (cambio)
      let adminBRL = null;
      let adminPYG = null;
      try {
        const storeRes = await fetch(`${API_BASE}/store-info/`);
        if (storeRes.ok) {
          const storeData = await storeRes.json();
          if (storeData.cambio_usd_brl) adminBRL = parseFloat(storeData.cambio_usd_brl);
          if (storeData.cambio_usd_pyg) adminPYG = parseFloat(storeData.cambio_usd_pyg);
        }
      } catch (e) {
        console.log('Could not fetch admin cambio rates, will use real-time API');
      }

      // Step 2: If both admin rates set, use them entirely
      if (adminBRL && adminPYG) {
        const newRates = { BRL: adminBRL, PYG: adminPYG };
        setRates(newRates);
        setLastUpdated(new Date());
        setRateSource('admin');
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          rates: newRates,
          timestamp: Date.now(),
        }));
        return;
      }

      // Step 3: Fetch real-time rates from free API
      let apiBRL = null;
      let apiPYG = null;
      try {
        const res = await fetch(EXCHANGE_API);
        const data = await res.json();
        if (data.result === 'success' && data.rates) {
          apiBRL = data.rates.BRL || null;
          apiPYG = data.rates.PYG || null;
        }
      } catch (e) {
        console.log('Exchange rate API fetch failed');
      }

      // Step 4: Combine — admin override takes priority per currency, then API, then fallback
      const newRates = {
        BRL: adminBRL || apiBRL || rates.BRL || 5.20,
        PYG: adminPYG || apiPYG || rates.PYG || 7300,
      };

      const source = (adminBRL || adminPYG) ? 'admin' : (apiBRL || apiPYG) ? 'api' : 'fallback';

      setRates(newRates);
      setLastUpdated(new Date());
      setRateSource(source);
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        rates: newRates,
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.log('Exchange rate fetch failed, using cached/fallback rates');
      setRateSource('fallback');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
    // Refresh every 30 minutes
    const interval = setInterval(fetchRates, CACHE_DURATION);
    return () => clearInterval(interval);
  }, [fetchRates]);

  // Convert USD to another currency
  const convertFromUSD = useCallback((amountUSD, toCurrency = 'BRL') => {
    const rate = rates[toCurrency] || 1;
    return amountUSD * rate;
  }, [rates]);

  // Format price always in USD
  const formatUSD = useCallback((price) => {
    const num = parseFloat(price) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  }, []);

  // Format a USD amount as BRL equivalent
  const formatAsBRL = useCallback((priceUSD) => {
    const num = parseFloat(priceUSD) || 0;
    const brl = num * (rates.BRL || 5.20);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(brl);
  }, [rates]);

  return (
    <CurrencyContext.Provider value={{
      rates,
      loading,
      lastUpdated,
      rateSource,
      convertFromUSD,
      formatUSD,
      formatAsBRL,
      refreshRates: fetchRates,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
